import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { HallwayUniverse } from '@/types/universe'
import {
  BILLIONAIRE_RELAYS,
  DeployNsiteParams,
  NsiteDeployStage,
  deployNsite,
  gatewayUrlsFor
} from '@/services/nsite-deploy.service'
import { accountAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  Loading03Icon,
  SourceCodeIcon
} from '@hugeicons/core-free-icons'
import { useAtomValue } from 'jotai'
import { forwardRef, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { isValidDTag } from '@/lib/base36'

// Mirror of deployer/stringFields: simple string values.
const SIMPLE_STRING_FIELDS: (keyof HallwayUniverse)[] = [
  'appName',
  'baseShareURL',
  'homeMarkdown',
  'defaultPrimaryColor',
  'defaultThemeSetting',
  'notificationListStyleDefault',
  'noteListStyleDefault',
  'imgproxyUrl',
  'imgproxyKey',
  'imgproxySalt',
  'pomegranateCentral',
  'translationsURL',
  'registryOfKindsURL',
  'linkPreviewService',
  'genericCorsProxy',
  'avatarStyleDefault'
]

// Mirror of deployer/stringListFields: one URL/string per line.
const LIST_FIELDS: (keyof HallwayUniverse)[] = [
  'defaultFavoriteRelays',
  'defaultBlossomServers',
  'defaultNostrConnectRelays',
  'bigRelayUrls',
  'indexerUrls',
  'defaultInbox',
  'defaultOutbox',
  'noteSearchRelayUrls',
  'noteSearchRelayUrlsExtra',
  'profileSearchRelayUrls',
  'readRepliesFrom',
  'publishTargets',
  'pomegranateOperators'
]

// Mirror of deployer/boolFields.
const BOOL_FIELDS: (keyof HallwayUniverse)[] = [
  'groupedDefault',
  'includeRepliesDefault',
  'sidebarCollapseDefault',
  'enableSingleColumnLayoutDefault',
  'readRepliesFromExclusive',
  'publishTargetsExclusive',
  'dearrowYoutube',
  'hideNotificationsDefault',
  'hideConversationsDefault',
  'hideFollowingDefault',
  'hideFavoriteRelaysDefault'
]

const PRIMARY_COLOR_KEYS = [
  'DEFAULT',
  'RED',
  'ORANGE',
  'AMBER',
  'YELLOW',
  'LIME',
  'GREEN',
  'EMERALD',
  'TEAL',
  'CYAN',
  'SKY',
  'BLUE',
  'INDIGO',
  'VIOLET',
  'PURPLE',
  'FUCHSIA',
  'PINK',
  'ROSE'
] as const

const THEME_KEYS = ['system', 'light', 'dark'] as const
const AVATAR_KEYS = ['circle', 'square'] as const

// Helper: turn an array into "url per line" form state and back.
function listToString(arr: string[] | undefined): string {
  return (arr ?? []).join('\n')
}
function stringToList(s: string): string[] {
  return s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
}

function fieldLabel(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

// Build a deep-cloned form-state object out of the current universe.
function makeInitialState(u: HallwayUniverse) {
  return {
    ...u,
    hardcodedFeeds:
      u.hardcodedFeeds?.map((f) => ({
        name: f.name ?? '',
        icon: f.icon ?? '',
        relays: (f.relays ?? []).slice(),
        pubkeys: (f.pubkeys ?? []).slice(),
        kinds: (f.kinds ?? []).slice()
      })) ?? [],
    kindGroups:
      u.kindGroups?.map((g) => ({
        kinds: g.kinds.slice(),
        label: g.label ?? '',
        disabled: g.disabled ?? false
      })) ?? [],
    clientHandlers: {
      fallback: u.clientHandlers?.fallback?.map((h) => ({ ...h })) ?? [],
      byKind: Object.entries(u.clientHandlers?.byKind ?? {}).flatMap(([k, hs]) =>
        (hs as { name: string; urlPattern: string }[]).map((h) => ({ kind: k, ...h }))
      )
    },
    translations: { ...(u.translations ?? {}) }
  }
}

// Filter out empty fields so the resulting universe stays close to the
// deployer/server contract: no `field: ''` entries for unset strings.
function compactForUniverse(s: ReturnType<typeof makeInitialState>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of SIMPLE_STRING_FIELDS as string[]) {
    const v = (s as Record<string, unknown>)[f]
    if (typeof v === 'string' && v.trim()) out[f] = v.trim()
  }
  for (const f of LIST_FIELDS as string[]) {
    const arr = (s as Record<string, unknown>)[f]
    if (Array.isArray(arr) && arr.length) out[f] = arr
  }
  for (const f of BOOL_FIELDS as string[]) {
    if ((s as Record<string, unknown>)[f]) out[f] = true
  }
  if (s.feedKinds?.length) out.feedKinds = s.feedKinds
  if (s.groupedTimeframe) out.groupedTimeframe = s.groupedTimeframe
  if (s.maxEmbedDepth) out.maxEmbedDepth = s.maxEmbedDepth
  if (s.logoUrl) out.logoUrl = s.logoUrl
  if (s.hardcodedFeeds?.length) {
    const feeds: {
      name?: string
      icon?: string
      relays?: string[]
      pubkeys?: string[]
      kinds?: number[]
    }[] = []
    for (const raw of s.hardcodedFeeds) {
      const feed: (typeof feeds)[number] = {}
      if ((raw.name as string | undefined)?.trim()) feed.name = (raw.name as string).trim()
      if ((raw.icon as string | undefined)?.trim()) feed.icon = (raw.icon as string).trim()
      const relays =
        (raw.relays as string[] | undefined)?.map((r) => r.trim()).filter(Boolean) ?? []
      if (relays.length) feed.relays = relays
      const pubkeys =
        (raw.pubkeys as string[] | undefined)?.map((p) => p.trim()).filter(Boolean) ?? []
      if (pubkeys.length) feed.pubkeys = pubkeys
      const kinds = (raw.kinds as number[] | undefined)?.filter((n) => Number.isInteger(n)) ?? []
      if (kinds.length) feed.kinds = kinds
      if (Object.keys(feed).length) feeds.push(feed)
    }
    if (feeds.length) out.hardcodedFeeds = feeds
  }
  if (s.kindGroups?.length) out.kindGroups = s.kindGroups
  if (s.clientHandlers.fallback.length || s.clientHandlers.byKind.length) {
    const ch: { fallback?: typeof s.clientHandlers.fallback; byKind?: Record<string, unknown[]> } =
      {}
    if (s.clientHandlers.fallback.length) ch.fallback = s.clientHandlers.fallback
    const bk: Record<string, unknown[]> = {}
    for (const e of s.clientHandlers.byKind) {
      if (!e.kind) continue
      if (!bk[e.kind]) bk[e.kind] = []
      bk[e.kind].push({ name: e.name, urlPattern: e.urlPattern })
    }
    if (Object.keys(bk).length) ch.byKind = bk
    if (Object.keys(ch).length) out.clientHandlers = ch
  }
  if (Object.keys(s.translations ?? {}).length) out.translations = s.translations
  return out
}

const ForkPage = forwardRef(({ index }: { index?: number }, ref) => {
  const u = window.hallway.universe
  const account = useAtomValue(accountAtom)

  useEffect(() => {
    document.title = `Fork | ${u.appName}`
    history.replaceState(null, '', '/fork')
  }, [u.appName])

  // Nsite-specific fields. Source URL fetching is intentionally stubbed: we
  // only ever fetch /index.html from the source URL today. The full file-list
  // walker is left as a future extension.
  const [nsiteId, setNsiteId] = useState('fork')
  const [nsiteTitle, setNsiteTitle] = useState(u.appName)
  const [nsiteDescription, setNsiteDescription] = useState('')
  const [sourceURL, setSourceURL] = useState('')

  // Form state mirrors window.hallway.universe.
  const [state, setState] = useState(() => makeInitialState(u))

  // Render lists as newline-separated strings so the Textarea-based UI stays
  // simple. These are derived from `state` and used by the inputs.
  const [lists, setLists] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    for (const f of LIST_FIELDS) init[f] = listToString(state[f] as string[] | undefined)
    return init
  })

  function setField<K extends keyof HallwayUniverse>(key: K, value: HallwayUniverse[K]) {
    setState((s) => ({ ...s, [key]: value }))
  }

  function setListField(key: string, value: string) {
    setLists((l) => ({ ...l, [key]: value }))
    setState((s) => ({ ...s, [key]: stringToList(value) }))
  }

  // --- hardcoded feeds editor ---------------------------------------------

  const feeds =
    (state.hardcodedFeeds as ReturnType<typeof makeInitialState>['hardcodedFeeds']) ?? []

  function setFeeds(next: typeof feeds) {
    setState((s) => ({ ...s, hardcodedFeeds: next }))
  }

  function updateFeed(index: number, patch: Partial<(typeof feeds)[number]>) {
    setFeeds(feeds.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  function setFeedListField(index: number, field: 'relays' | 'pubkeys' | 'kinds', text: string) {
    if (field === 'kinds') {
      const nums = text
        .split('\n')
        .map((x) => parseInt(x.trim(), 10))
        .filter((n) => Number.isInteger(n))
      updateFeed(index, { kinds: nums })
    } else {
      updateFeed(index, { [field]: stringToList(text) })
    }
  }

  function addFeed() {
    setFeeds([...feeds, { name: '', icon: '', relays: [], pubkeys: [], kinds: [] }])
  }

  function removeFeed(index: number) {
    setFeeds(feeds.filter((_, i) => i !== index))
  }

  // Stages of the deploy pipeline. We keep the latest stage for rendering,
  // and accumulate a simple log for the user.
  const [stage, setStage] = useState<NsiteDeployStage>({ stage: 'idle' })
  const [log, setLog] = useState<string[]>([])
  const [result, setResult] = useState<ReturnType<typeof gatewayUrlsFor> | null>(null)
  const [busy, setBusy] = useState(false)

  function stageToText(s: NsiteDeployStage): string {
    switch (s.stage) {
      case 'idle':
        return 'idle'
      case 'fetching':
        return `fetching ${s.url}`
      case 'injecting':
        return 'injecting universe JSON into index.html'
      case 'uploading':
        return `uploading ${s.file} → ${s.server}`
      case 'mirroring':
        return `mirroring ${s.file} → ${s.server}`
      case 'signing':
        return 'signing nsite event'
      case 'publishing':
        return `publishing to ${s.relay} (${s.done}/${s.total})`
      case 'done':
        return `done — event ${s.eventId.slice(0, 8)}…`
      case 'error':
        return `error: ${s.error}`
    }
  }

  function stageIsError(s: NsiteDeployStage): boolean {
    return s.stage === 'error'
  }

  function isStageRunning(s: NsiteDeployStage): boolean {
    return s.stage !== 'idle' && s.stage !== 'done' && s.stage !== 'error'
  }

  async function deploy() {
    if (!account) {
      toast.error('log in first — nsite publishing needs a signing key')
      return
    }
    if (!isValidDTag(nsiteId)) {
      toast.error(
        'nsite id must be 1-13 chars of lowercase letters, digits or "-", and not end with "-"'
      )
      return
    }
    if (!sourceURL && !state.appName) {
      toast.error('set a source URL or fill in appName at minimum')
      return
    }

    setBusy(true)
    setResult(null)
    setLog([])
    setStage({ stage: 'fetching', url: (sourceURL || '') + '/index.html' })

    const universePayload = compactForUniverse(state)

    const params: DeployNsiteParams = {
      d: nsiteId,
      description: nsiteDescription,
      title: nsiteTitle,
      sourceURL: sourceURL || undefined,
      universe: universePayload,
      extraBlossomServers: stringToList(lists.defaultBlossomServers)
    }

    try {
      await deployNsite(params, (s) => {
        setStage(s)
        setLog((l) => [...l, stageToText(s)])
      })
      setResult(gatewayUrlsFor(account.pubkey, nsiteId))
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setStage({ stage: 'error', error: msg })
      setLog((l) => [...l, `error: ${msg}`])
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <SecondaryPageLayout ref={ref} index={index} title="Fork as nsite">
      <ScrollArea className="h-[calc(100vh-3rem)]">
        <div className="px-4 py-4 max-w-3xl mx-auto space-y-6 pb-20">
          {/* Header */}
          <header className="space-y-2">
            <h1 className="text-2xl font-bold">Fork as nsite</h1>
            <p className="text-sm text-muted-foreground">
              Publish this instance as a <strong>NIP-5A nsite</strong>: the current universe config
              gets serialized into the deployed
              <code className="mx-1 text-xs bg-muted px-1 rounded">index.html</code>
              and a kind-35128 manifest event is signed and broadcast to your write relays, the big
              relays, and the 44billion relay set.
            </p>
          </header>

          {/* Section: Nsite identification */}
          <Section
            title="Nsite"
            description="Site identifier used as the nsite `d` tag and as the subdomain on nsite gateways."
          >
            <Field
              label="Nsite ID"
              hint="1-13 chars: lowercase letters, digits, '-'. Cannot end with '-'."
            >
              <Input
                value={nsiteId}
                onChange={(e) => setNsiteId(e.target.value)}
                placeholder="fork"
              />
            </Field>
            <Field label="Title" hint="Human-readable title (becomes the `title` tag).">
              <Input
                value={nsiteTitle}
                onChange={(e) => setNsiteTitle(e.target.value)}
                placeholder={u.appName}
              />
            </Field>
            <Field label="Description" hint="Short description (becomes the `description` tag).">
              <Textarea
                rows={3}
                value={nsiteDescription}
                onChange={(e) => setNsiteDescription(e.target.value)}
                placeholder="A Hallway instance customised for …"
              />
            </Field>
            <Field
              label="Source URL"
              hint={
                <>
                  URL of a deployed Hallway (or similar) to fork files from. We currently only fetch{' '}
                  <code className="text-xs bg-muted px-1 rounded">/index.html</code>; the full
                  asset-list walker is left as a future extension.
                </>
              }
            >
              <Input
                value={sourceURL}
                onChange={(e) => setSourceURL(e.target.value)}
                placeholder="https://example.com"
              />
            </Field>
          </Section>

          {/* Section: Branding */}
          <Section title="Branding" description="App identity baked into the deployed index.html.">
            <Field label="App Name">
              <Input
                value={(state.appName as string) ?? ''}
                onChange={(e) => setField('appName', e.target.value)}
              />
            </Field>
            <Field label="Logo URL" hint="URL or `/assets/logo.png` on the deployed site.">
              <Input
                value={state.logoUrl ?? ''}
                onChange={(e) => setField('logoUrl', e.target.value)}
              />
            </Field>
            <Field label="Base Share URL">
              <Input
                value={(state.baseShareURL as string) ?? ''}
                onChange={(e) => setField('baseShareURL', e.target.value)}
              />
            </Field>
            <Field label="Home Markdown">
              <Textarea
                rows={6}
                value={(state.homeMarkdown as string) ?? ''}
                onChange={(e) => setField('homeMarkdown', e.target.value)}
              />
            </Field>
          </Section>

          {/* Section: Appearance defaults */}
          <Section
            title="User Defaults"
            description="Initial values for the toggle/preference UI shown to new users."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Default Theme">
                <Select
                  value={(state.defaultThemeSetting as string) ?? 'system'}
                  onValueChange={(v) =>
                    setField('defaultThemeSetting', v as HallwayUniverse['defaultThemeSetting'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {THEME_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Default Primary Color">
                <Select
                  value={(state.defaultPrimaryColor as string) ?? 'TEAL'}
                  onValueChange={(v) =>
                    setField('defaultPrimaryColor', v as HallwayUniverse['defaultPrimaryColor'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIMARY_COLOR_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Notification List Style">
                <Select
                  value={(state.notificationListStyleDefault as string) ?? 'compact'}
                  onValueChange={(v) =>
                    setField(
                      'notificationListStyleDefault',
                      v as HallwayUniverse['notificationListStyleDefault']
                    )
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['', 'compact', 'detailed'] as const).map((k) => (
                      <SelectItem key={k || 'unset'} value={k}>
                        {k || 'unset'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Normal Feed Style">
                <Select
                  value={(state.noteListStyleDefault as string) ?? 'full'}
                  onValueChange={(v) =>
                    setField('noteListStyleDefault', v as HallwayUniverse['noteListStyleDefault'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(['', 'full', 'detailed', 'compact'] as const).map((k) => (
                      <SelectItem key={k || 'unset'} value={k}>
                        {k || 'unset'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Default Avatar Style">
                <Select
                  value={(state.avatarStyleDefault as string) ?? 'square'}
                  onValueChange={(v) =>
                    setField('avatarStyleDefault', v as HallwayUniverse['avatarStyleDefault'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVATAR_KEYS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {BOOL_FIELDS.map((f) => (
                <BoolField
                  key={f}
                  label={fieldLabel(f as string)}
                  checked={!!state[f]}
                  onChange={(v) => setField(f, v as HallwayUniverse[typeof f])}
                />
              ))}
            </div>
          </Section>

          {/* Section: Feeds & Kinds */}
          <Section
            title="Feeds & Kinds"
            description="Feed kinds synced from the user's follow list and the grouping used by the kind filter UI."
          >
            <Field
              label="Feed Kinds"
              hint="Event kind numbers that will be synced from the user's follow list. One number per line."
            >
              <Textarea
                rows={5}
                value={(state.feedKinds ?? []).join('\n')}
                onChange={(e) =>
                  setField(
                    'feedKinds',
                    e.target.value
                      .split('\n')
                      .map((x) => parseInt(x.trim(), 10))
                      .filter((n) => Number.isInteger(n))
                  )
                }
              />
            </Field>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Fixed Feeds</span>
                <Button variant="outline" size="sm" type="button" onClick={addFeed}>
                  Add feed
                </Button>
              </div>
              {feeds.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No fixed feeds configured. Add one to pin feeds to the sidebar.
                </p>
              )}
              {feeds.map((feed, fi) => (
                <div
                  key={fi}
                  className="border border-input rounded-lg p-3 space-y-3 bg-background"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Feed {fi + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFeed(fi)}
                      className="text-xs text-muted-foreground hover:text-destructive"
                    >
                      remove
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name">
                      <Input
                        value={feed.name as string}
                        onChange={(e) => updateFeed(fi, { name: e.target.value })}
                        placeholder="Local News"
                      />
                    </Field>
                    <Field label="Icon URL">
                      <Input
                        value={feed.icon as string}
                        onChange={(e) => updateFeed(fi, { icon: e.target.value })}
                        placeholder="https://icons.com/news.png"
                      />
                    </Field>
                  </div>
                  <Field label="Relay URLs" hint="One wss:// URL per line.">
                    <Textarea
                      rows={2}
                      value={(feed.relays as string[]).join('\n')}
                      onChange={(e) => setFeedListField(fi, 'relays', e.target.value)}
                      placeholder="wss://relay.example.com"
                    />
                  </Field>
                  <Field label="Pubkeys" hint="npub1... or hex, one per line.">
                    <Textarea
                      rows={2}
                      value={(feed.pubkeys as string[]).join('\n')}
                      onChange={(e) => setFeedListField(fi, 'pubkeys', e.target.value)}
                      placeholder="npub1..."
                    />
                  </Field>
                  <Field
                    label="Kinds"
                    hint="Restrict this feed to specific event kinds, one number per line (e.g. 30311 for livestreams). Leave empty for all default feed kinds."
                  >
                    <Textarea
                      rows={2}
                      value={(feed.kinds as number[]).join('\n')}
                      onChange={(e) => setFeedListField(fi, 'kinds', e.target.value)}
                      placeholder="30311"
                    />
                  </Field>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Grouped Timeframe (seconds)">
                <Input
                  type="number"
                  value={(state.groupedTimeframe as number) ?? ''}
                  onChange={(e) => setField('groupedTimeframe', parseInt(e.target.value, 10) || 0)}
                />
              </Field>
              <Field label="Max Embed Depth">
                <Input
                  type="number"
                  value={(state.maxEmbedDepth as number) ?? ''}
                  onChange={(e) => setField('maxEmbedDepth', parseInt(e.target.value, 10) || 0)}
                />
              </Field>
            </div>
          </Section>

          {/* Section: Relays */}
          <Section
            title="Relays"
            description="Relays this instance defaults to for various tasks. One URL per line."
          >
            {LIST_FIELDS.filter((f) =>
              [
                'defaultFavoriteRelays',
                'defaultBlossomServers',
                'defaultNostrConnectRelays',
                'bigRelayUrls',
                'indexerUrls',
                'defaultInbox',
                'defaultOutbox',
                'noteSearchRelayUrls',
                'noteSearchRelayUrlsExtra',
                'profileSearchRelayUrls'
              ].includes(f as string)
            ).map((f) => (
              <Field key={f} label={fieldLabel(f as string)}>
                <Textarea
                  rows={3}
                  value={lists[f as string] ?? ''}
                  onChange={(e) => setListField(f as string, e.target.value)}
                />
              </Field>
            ))}
          </Section>

          {/* Section: Reply Sources */}
          <Section title="Reply Sources">
            <Field
              label="Read Replies From"
              hint="Extra relay URLs to read replies from; appended to user's read relays when non-exclusive."
            >
              <Textarea
                rows={3}
                value={lists.readRepliesFrom}
                onChange={(e) => setListField('readRepliesFrom', e.target.value)}
              />
            </Field>
            <BoolField
              label="Read Replies From Exclusive"
              checked={!!state.readRepliesFromExclusive}
              onChange={(v) =>
                setField(
                  'readRepliesFromExclusive',
                  v as HallwayUniverse['readRepliesFromExclusive']
                )
              }
            />
          </Section>

          {/* Section: Publish Targets */}
          <Section title="Publish Targets">
            <Field
              label="Publish Targets"
              hint="Extra relay URLs that new posts are published to by default."
            >
              <Textarea
                rows={3}
                value={lists.publishTargets}
                onChange={(e) => setListField('publishTargets', e.target.value)}
              />
            </Field>
            <BoolField
              label="Publish Targets Exclusive"
              checked={!!state.publishTargetsExclusive}
              onChange={(v) =>
                setField('publishTargetsExclusive', v as HallwayUniverse['publishTargetsExclusive'])
              }
            />
          </Section>

          {/* Section: Services & Proxies */}
          <Section title="Services & Proxies">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Imgproxy URL">
                <Input
                  value={(state.imgproxyUrl as string) ?? ''}
                  onChange={(e) => setField('imgproxyUrl', e.target.value)}
                />
              </Field>
              <Field label="Link Preview Service">
                <Input
                  value={(state.linkPreviewService as string) ?? ''}
                  onChange={(e) => setField('linkPreviewService', e.target.value)}
                />
              </Field>
              <Field label="Generic CORS Proxy">
                <Input
                  value={(state.genericCorsProxy as string) ?? ''}
                  onChange={(e) => setField('genericCorsProxy', e.target.value)}
                />
              </Field>
              <Field label="Registry of Kinds URL">
                <Input
                  value={(state.registryOfKindsURL as string) ?? ''}
                  onChange={(e) => setField('registryOfKindsURL', e.target.value)}
                />
              </Field>
              <Field label="Pomegranate Central">
                <Input
                  value={(state.pomegranateCentral as string) ?? ''}
                  onChange={(e) => setField('pomegranateCentral', e.target.value)}
                />
              </Field>
              <Field label="Translations URL">
                <Input
                  value={(state.translationsURL as string) ?? ''}
                  onChange={(e) => setField('translationsURL', e.target.value)}
                />
              </Field>
            </div>
            <Field label="Pomegranate Operators">
              <Textarea
                rows={3}
                value={lists.pomegranateOperators}
                onChange={(e) => setListField('pomegranateOperators', e.target.value)}
              />
            </Field>
          </Section>

          {/* Section: Publish to ... */}
          <Section
            title="Publish Targets (info)"
            description="Where the nsite event will be broadcast on click of Deploy."
          >
            <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">
              <li>Your write relays (kind-10002 list)</li>
              <li>
                The general-purpose relays in{' '}
                <code className="bg-muted px-1 rounded">window.hallway.universe.bigRelayUrls</code>
              </li>
              {BILLIONAIRE_RELAYS.map((r) => (
                <li key={r}>
                  <code className="bg-muted px-1 rounded">{r}</code>
                </li>
              ))}
            </ul>
          </Section>

          {/* Deploy button + status */}
          <div className="sticky bottom-0 bg-background/80 backdrop-blur -mx-4 px-4 py-3 border-t border-border flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Button onClick={deploy} disabled={busy || !account} className="flex-1">
                {busy ? (
                  <>
                    <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                    deploying…
                  </>
                ) : (
                  <>
                    <HugeiconsIcon icon={SourceCodeIcon} className="size-4" />
                    deploy as nsite
                  </>
                )}
              </Button>
            </div>

            {!account && (
              <p className="text-xs text-destructive">
                You need to log in (any signer except{' '}
                <code className="bg-muted px-1 rounded">npub</code>) to deploy.
              </p>
            )}

            {stage.stage !== 'idle' && (
              <div className="border rounded-lg p-3 bg-card space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  {isStageRunning(stage) ? (
                    <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                  ) : stage.stage === 'done' ? (
                    <HugeiconsIcon icon={CheckmarkCircle02Icon} className="size-4 text-green-500" />
                  ) : stageIsError(stage) ? (
                    <HugeiconsIcon icon={ArrowDown01Icon} className="size-4 text-destructive" />
                  ) : null}
                  <code className="text-xs">{stageToText(stage)}</code>
                </div>
                {log.length > 0 && (
                  <pre className="text-[10px] leading-snug text-muted-foreground max-h-32 overflow-auto whitespace-pre-wrap break-all">
                    {log.join('\n')}
                  </pre>
                )}
              </div>
            )}

            {result && (
              <div className="border rounded-lg p-3 bg-card space-y-2">
                <p className="text-sm font-medium">Published. Open your nsite at:</p>
                <ul className="space-y-1">
                  {result.map((g) => (
                    <li key={g.url} className="flex items-center gap-2 text-sm">
                      <HugeiconsIcon
                        icon={ArrowRight01Icon}
                        className="size-4 text-muted-foreground"
                      />
                      <a
                        className="text-blue-600 hover:underline break-all"
                        target="_blank"
                        rel="noreferrer"
                        href={g.url}
                      >
                        {g.label} — {g.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ScrollArea>
    </SecondaryPageLayout>
  )
})

ForkPage.displayName = 'ForkPage'
export default ForkPage

// --- Small UI helpers ---------------------------------------------------------

function Section({
  title,
  description,
  children
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <fieldset className="border border-neutral-200 bg-neutral-50/40 dark:bg-neutral-900/40 rounded-xl p-4 space-y-3">
      <legend className="text-sm font-semibold px-2">{title}</legend>
      {description && <p className="text-xs text-muted-foreground -mt-2">{description}</p>}
      <div className="space-y-3">{children}</div>
    </fieldset>
  )
}

function Field({
  label,
  hint,
  children
}: {
  label: string
  hint?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[10px] leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  )
}

function BoolField({
  label,
  checked,
  onChange
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-xs">{label}</span>
    </label>
  )
}
